import { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Target, Plus, Search, MessageSquare, Phone, Mail, Instagram, Linkedin,
  Sparkles, Flame, ThermometerSun, Globe, MapPin, TrendingUp, Upload, Zap,
  ShieldCheck, Send, Loader2, CheckCircle2, XCircle, X,
  Calendar, Clock, Pause, Play, Trash2, RotateCcw,
} from 'lucide-react';
import { toast } from 'sonner';

const STAGES = ['discovered', 'qualified', 'contacted', 'demo_scheduled', 'demo_done', 'won', 'lost'];
const STAGE_LABELS = {
  discovered: 'Discovered', qualified: 'Qualified', contacted: 'Contacted',
  demo_scheduled: 'Demo Scheduled', demo_done: 'Demo Done', won: 'Won', lost: 'Lost',
};
const stageMeta = {
  discovered: 'bg-slate-100 text-slate-700 border-slate-300',
  qualified: 'bg-blue-100 text-blue-700 border-blue-300',
  contacted: 'bg-violet-100 text-violet-700 border-violet-300',
  demo_scheduled: 'bg-amber-100 text-amber-800 border-amber-300',
  demo_done: 'bg-orange-100 text-orange-800 border-orange-300',
  won: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  lost: 'bg-rose-100 text-rose-700 border-rose-300',
};
const tierMeta = {
  premium: { color: 'bg-gradient-to-r from-amber-400 to-yellow-500 text-white', label: 'Premium', icon: Sparkles },
  growing: { color: 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white', label: 'Growing', icon: TrendingUp },
  small: { color: 'bg-slate-200 text-slate-700', label: 'Small', icon: Target },
};
const tempMeta = {
  hot: { color: 'text-rose-600', label: 'Hot', icon: Flame },
  warm: { color: 'text-amber-600', label: 'Warm', icon: ThermometerSun },
  cold: { color: 'text-slate-500', label: 'Cold', icon: ThermometerSun },
};

const emptyForm = () => ({
  name: '',
  sport_type: '',
  phone: '',
  email: '',
  website: '',
  instagram: '',
  city: 'Bengaluru',
  state: 'Karnataka',
  reviews_count: 0,
  has_multi_branch: false,
  has_turf: false,
  multi_sport: false,
  active_social_media: false,
  // Phase 89-Lead-Intel-WhatsApp — Meta WhatsApp Business policy
  // requires explicit opt-in before a Business-Initiated Conversation
  // outside the 24h customer-service window. Default to `false` and
  // require an explicit operator/visitor toggle.
  opt_in_consent: false,
  outreach_platform: 'WhatsApp',
  notes: '',
});

export default function LeadIntelligence() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ tier: 'all', stage: 'all', sport: 'all', search: '' });
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [importCSV, setImportCSV] = useState('');
  const [importing, setImporting] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (filters.tier !== 'all') params.tier = filters.tier;
      if (filters.stage !== 'all') params.pipeline_stage = filters.stage;
      if (filters.sport !== 'all') params.sport_type = filters.sport;
      const [leadsRes, statsRes] = await Promise.all([
        api.get('/admin/leads', { params }),
        api.get('/admin/leads/stats'),
      ]);
      setLeads(leadsRes.data || []);
      setStats(statsRes.data);
    } catch {
      toast.error('Failed to load Lead Intelligence');
    } finally {
      setLoading(false);
    }
  }, [filters.tier, filters.stage, filters.sport]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => { setEditing(null); setForm(emptyForm()); setShowForm(true); };
  const openEdit = (lead) => {
    setEditing(lead);
    setForm({
      name: lead.name || '',
      sport_type: lead.sport_type || '',
      phone: lead.phone || '',
      email: lead.email || '',
      website: lead.website || '',
      instagram: lead.instagram || '',
      city: lead.city || '',
      state: lead.state || 'Karnataka',
      reviews_count: lead.reviews_count || 0,
      has_multi_branch: !!lead.has_multi_branch,
      has_turf: !!lead.has_turf,
      multi_sport: !!lead.multi_sport,
      active_social_media: !!lead.active_social_media,
      opt_in_consent: !!lead.opt_in_consent,
      outreach_platform: lead.outreach_platform || 'WhatsApp',
      notes: lead.notes || '',
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/leads/${editing.id}`, form);
        toast.success('Lead updated');
      } else {
        await api.post('/admin/leads', form);
        toast.success('Lead added');
      }
      setShowForm(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleStageChange = async (leadId, stage) => {
    try {
      await api.put(`/admin/leads/${leadId}`, { pipeline_stage: stage });
      toast.success(`Stage → ${STAGE_LABELS[stage]}`);
      fetchData();
    } catch (err) {
      toast.error('Failed to advance');
    }
  };

  // Phase 106 — Auto-onboarding loop. One-click conversion from Won lead → live academy.
  const [convertingId, setConvertingId] = useState(null);
  const [conversionResult, setConversionResult] = useState(null);

  const handleConvertToCustomer = async (lead) => {
    if (!window.confirm(`Convert "${lead.name}" into a live Out-Play academy and send the magic-login invite?`)) return;
    setConvertingId(lead.id);
    try {
      const res = await api.post(`/admin/leads/${lead.id}/convert-to-customer`, {
        tier: 'starter',
        send_whatsapp: !!lead.phone,
        send_email: !!lead.email,
      });
      setConversionResult({ lead, result: res.data });
      toast.success(`🎉 ${lead.name} is now a customer · academy id ${(res.data.academy_id || '').slice(0, 8)}…`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Conversion failed');
    } finally {
      setConvertingId(null);
    }
  };

  const handleWhatsApp = async (lead) => {
    if (!lead.phone) { toast.error('No phone number on file'); return; }
    try {
      // Fetch the tier-appropriate pitch template
      const tplRes = await api.get(`/admin/leads/whatsapp-template/${lead.tier}`);
      const message = (tplRes.data?.template || '').replaceAll('{name}', lead.name);
      // Clean phone for wa.me — keep digits only, drop leading +
      const cleaned = (lead.phone || '').replace(/\D/g, '');
      const url = `https://wa.me/${cleaned}?text=${encodeURIComponent(message)}`;
      // Open WhatsApp web in a new tab (operator confirms + sends manually)
      window.open(url, '_blank', 'noopener,noreferrer');
      // Log the outreach attempt
      await api.post(`/admin/leads/${lead.id}/contact`, {
        channel: 'whatsapp',
        note: 'WhatsApp pitch opened',
        advance_to_stage: lead.pipeline_stage === 'discovered' || lead.pipeline_stage === 'qualified' ? 'contacted' : undefined,
      });
      toast.success('WhatsApp opened + outreach logged');
      fetchData();
    } catch (err) {
      toast.error('Failed to open WhatsApp');
    }
  };

  // Phase 89-Lead-Intel-WhatsApp — one-click compliant ping via the
  // platform AiSensy gateway. Distinct from `handleWhatsApp` (which
  // opens wa.me for a manual send). `Ping` fires an actual Meta-
  // approved template message instantly, but ONLY when the lead has
  // captured explicit opt-in consent.
  const [pingingId, setPingingId] = useState(null);
  const handleInstantPing = async (lead) => {
    if (!lead.opt_in_consent) {
      toast.error('Lead has not opted in to WhatsApp messaging. Capture consent via the edit dialog first.');
      return;
    }
    if (!lead.phone) {
      toast.error('No phone number on file.');
      return;
    }
    setPingingId(lead.id);
    try {
      const r = await api.post(`/admin/leads/${lead.id}/whatsapp-send`, {
        template_key: 'user_welcome',
      });
      const result = r?.data?.result || {};
      if (result.success) {
        toast.success(`Welcome ping queued for ${lead.name} · id=${result.message_id || 'n/a'}`);
      } else {
        const errText = String(result.error || 'unknown error');
        const campaignName = result.campaign_name || 'outplay_user_welcome';
        const missing = /campaign\s+does\s+not\s+exist|campaign\s+not\s+found|no\s+such\s+campaign|invalid\s+campaign\s+name/i.test(errText);
        if (missing) {
          toast.error(
            `AiSensy could not find a campaign named "${campaignName}". Open app.aisensy.com → Campaigns → Launch Campaign and create it.`,
            {
              duration: 14000,
              action: { label: 'Open AiSensy', onClick: () => window.open('https://app.aisensy.com/', '_blank', 'noopener') },
            },
          );
        } else {
          toast.error(`Gateway rejected: ${errText}`);
        }
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ping failed.');
    } finally {
      setPingingId(null);
    }
  };

  // Phase 89-Lead-Intel-Bulk — fan-out version of `handleInstantPing`.
  // The modal opens, previews the eligible opted-in count, fires the
  // background batch, then polls `/bulk-whatsapp-status/{batch_id}` every
  // 2s until the worker reports `complete`.
  const BULK_TEMPLATES = ['user_welcome', 'academy_welcome', 'subscription_reminder', 'fee_invoice_reminder'];
  const [showBulkPing, setShowBulkPing] = useState(false);
  const [bulkFilters, setBulkFilters] = useState({ template_key: 'user_welcome', temperature: 'any', pipeline_stage: 'any', tier: 'any' });
  const [bulkPreview, setBulkPreview] = useState({ eligible: 0, sample_names: [], loading: false });
  const [bulkBatch, setBulkBatch] = useState(null); // { batch_id, queued_count, processed, success_count, failure_count, status }
  const [bulkLaunching, setBulkLaunching] = useState(false);

  // Live eligible-count preview — fetched every time the modal opens or
  // the segment filters change. Uses the canonical async-IIFE pattern so
  // setState happens inside the IIFE (matches `TrialBanner.js`).
  useEffect(() => {
    if (!showBulkPing) return undefined;
    let active = true;
    (async () => {
      if (active) setBulkPreview((p) => ({ ...p, loading: true }));
      try {
        const params = {};
        if (bulkFilters.temperature && bulkFilters.temperature !== 'any') params.temperature = bulkFilters.temperature;
        if (bulkFilters.pipeline_stage && bulkFilters.pipeline_stage !== 'any') params.pipeline_stage = bulkFilters.pipeline_stage;
        if (bulkFilters.tier && bulkFilters.tier !== 'any') params.tier = bulkFilters.tier;
        const r = await api.get('/admin/leads/bulk-whatsapp-target-count', { params });
        if (active) setBulkPreview({ eligible: r.data?.eligible ?? 0, sample_names: r.data?.sample_names || [], loading: false });
      } catch {
        if (active) setBulkPreview({ eligible: 0, sample_names: [], loading: false });
      }
    })();
    return () => { active = false; };
  }, [showBulkPing, bulkFilters]);

  // Live progress polling — uses the canonical "ref snapshot + empty-deps
  // interval" pattern (mirrors `LivexHighlights.jsx:396`). The interval
  // mounts exactly once; we read the current batch from a ref so state
  // updates don't reset the timer, and we use a SINGLE direct setState
  // per tick to satisfy the strict `set-state-in-effect` rule. Completion
  // side-effects (toast + table refresh) live in a separate watcher
  // useEffect below.
  const bulkBatchRef = useRef(bulkBatch);
  useEffect(() => { bulkBatchRef.current = bulkBatch; }, [bulkBatch]);
  useEffect(() => {
    const tick = setInterval(async () => {
      const cur = bulkBatchRef.current;
      if (!cur || cur.status === 'complete') return;
      try {
        const r = await api.get(`/admin/leads/bulk-whatsapp-status/${cur.batch_id}`);
        setBulkBatch({ ...cur, ...(r.data || {}) });
      } catch (_e) { /* silent poll failure — next tick retries */ }
    }, 2000);
    return () => clearInterval(tick);
  }, []);

  // Completion watcher — once the batch flips to `complete`, fire the
  // success toast + refresh the leads table. Isolated so the polling
  // tick stays single-setState.
  const completedBatchRef = useRef(null);
  useEffect(() => {
    if (!bulkBatch || bulkBatch.status !== 'complete') return;
    if (completedBatchRef.current === bulkBatch.batch_id) return;
    completedBatchRef.current = bulkBatch.batch_id;
    toast.success(`Bulk dispatch complete · ${bulkBatch.success_count} sent · ${bulkBatch.failure_count} failed`);
    fetchData();
  }, [bulkBatch, fetchData]);

  const openBulkPing = () => {
    setBulkBatch(null);
    setBulkFilters({ template_key: 'user_welcome', temperature: 'any', pipeline_stage: 'any', tier: 'any' });
    setShowBulkPing(true);
  };

  const handleLaunchBulkPing = async () => {
    if (bulkPreview.eligible === 0) {
      toast.error('No eligible opted-in leads match the current segment.');
      return;
    }
    setBulkLaunching(true);
    try {
      const body = {
        template_key: bulkFilters.template_key,
      };
      if (bulkFilters.temperature && bulkFilters.temperature !== 'any') body.temperature = bulkFilters.temperature;
      if (bulkFilters.pipeline_stage && bulkFilters.pipeline_stage !== 'any') body.pipeline_stage = bulkFilters.pipeline_stage;
      if (bulkFilters.tier && bulkFilters.tier !== 'any') body.tier = bulkFilters.tier;
      const r = await api.post('/admin/leads/bulk-whatsapp-send', body);
      const data = r?.data || {};
      setBulkBatch({
        batch_id: data.batch_id,
        queued_count: data.queued_count,
        processed: 0,
        success_count: 0,
        failure_count: 0,
        skipped_consent: data.skipped_consent || 0,
        skipped_phone: data.skipped_phone || 0,
        status: 'queued',
      });
      toast.success(`Bulk batch queued · ${data.queued_count} opted-in leads · template "${data.template_key}"`);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Bulk dispatch failed';
      toast.error(detail);
    } finally {
      setBulkLaunching(false);
    }
  };

  const closeBulkPing = () => {
    setShowBulkPing(false);
    setBulkBatch(null);
    setBulkMode('now');
    setSchedulePayload({ kind: 'once', run_at_local: '', repeat: 'weekly', day_of_week: 'Mon', time_of_day: '09:00' });
    setLastSavedSchedule(null);
  };

  // Phase 89-Lead-Intel-Schedule — operator can flip the modal from
  // "Send now" into a Schedule + Repeat config builder. Keep this state
  // adjacent to the bulk-ping state so the modal stays the single source
  // of truth for both dispatch modes.
  const DAY_OPTIONS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const TIME_OPTIONS = ['07:00', '08:00', '09:00', '10:00', '12:00', '15:00', '17:00', '19:00'];
  const [bulkMode, setBulkMode] = useState('now'); // 'now' | 'schedule'
  const [schedulePayload, setSchedulePayload] = useState({
    kind: 'once',
    run_at_local: '',
    repeat: 'weekly',
    day_of_week: 'Mon',
    time_of_day: '09:00',
  });
  const [scheduleSubmitting, setScheduleSubmitting] = useState(false);
  const [lastSavedSchedule, setLastSavedSchedule] = useState(null); // {doc} or null
  const [scheduledCampaigns, setScheduledCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);

  // Helper to (re)load the scheduled campaigns panel. Mirrors the
  // structure of `fetchData` (setStates inside try only) which lints
  // clean under `react-hooks/set-state-in-effect`.
  const reloadScheduledCampaigns = useCallback(async () => {
    try {
      const r = await api.get('/admin/scheduled-campaigns', { params: { include_inactive: true } });
      setScheduledCampaigns(r.data?.campaigns || []);
    } catch {
      setScheduledCampaigns([]);
    } finally {
      setCampaignsLoading(false);
    }
  }, []);

  useEffect(() => { reloadScheduledCampaigns(); }, [reloadScheduledCampaigns]);

  const handleSaveSchedule = async () => {
    if (schedulePayload.kind === 'once' && !schedulePayload.run_at_local) {
      toast.error('Please pick a run date/time first.');
      return;
    }
    setScheduleSubmitting(true);
    try {
      const body = {
        template_key: bulkFilters.template_key,
        schedule_kind: schedulePayload.kind,
      };
      const filters = {};
      if (bulkFilters.temperature && bulkFilters.temperature !== 'any') filters.temperature = bulkFilters.temperature;
      if (bulkFilters.pipeline_stage && bulkFilters.pipeline_stage !== 'any') filters.pipeline_stage = bulkFilters.pipeline_stage;
      if (bulkFilters.tier && bulkFilters.tier !== 'any') filters.tier = bulkFilters.tier;
      body.filters = filters;
      if (schedulePayload.kind === 'once') {
        body.schedule_config = { run_at: schedulePayload.run_at_local };
      } else {
        body.schedule_config = {
          repeat: schedulePayload.repeat,
          day_of_week: schedulePayload.day_of_week,
          time_of_day: schedulePayload.time_of_day,
        };
      }
      const r = await api.post('/admin/scheduled-campaigns', body);
      const data = r?.data || {};
      setLastSavedSchedule({
        id: data.id,
        title: data.title,
        next_run_at: data.next_run_at,
        doc: data,
      });
      toast.success(`Schedule saved · next run ${new Date(data.next_run_at).toLocaleString()}`);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to save schedule';
      toast.error(detail);
    } finally {
      setScheduleSubmitting(false);
    }
  };

  // Watcher: when a schedule save completes, refresh the campaigns panel
  // AND close the Bulk Ping modal. Mirrors the `completedBatchRef` pattern
  // (ref-guard + single helper call) to satisfy
  // `react-hooks/set-state-in-effect`.
  const savedScheduleRef = useRef(null);
  useEffect(() => {
    if (!lastSavedSchedule?.id) return;
    if (savedScheduleRef.current === lastSavedSchedule.id) return;
    savedScheduleRef.current = lastSavedSchedule.id;
    reloadScheduledCampaigns();
    setShowBulkPing(false);
  }, [lastSavedSchedule, reloadScheduledCampaigns]);

  const handlePauseCampaign = async (id, isActive) => {
    try {
      await api.patch(`/admin/scheduled-campaigns/${id}`, { is_active: !isActive });
      toast.success(isActive ? 'Schedule paused' : 'Schedule resumed');
      reloadScheduledCampaigns();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  const handleDeleteCampaign = async (id) => {
    if (!window.confirm('Delete this scheduled campaign? Past dispatch audit rows are kept.')) return;
    try {
      await api.delete(`/admin/scheduled-campaigns/${id}`);
      toast.success('Schedule deleted');
      reloadScheduledCampaigns();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const handleBulkImport = async () => {
    if (!importCSV.trim()) { toast.error('Paste at least one row'); return; }
    setImporting(true);
    try {
      // CSV: name,sport,phone,city,website,reviews
      const rows = importCSV.trim().split('\n').map((line) => {
        const [name, sport_type, phone, city, website, reviews_count] = line.split(',').map(s => (s || '').trim());
        return { name, sport_type, phone, city, website, reviews_count: parseInt(reviews_count) || 0 };
      }).filter(r => r.name);
      const res = await api.post('/admin/leads/bulk-import', { leads: rows, source: 'csv_import' });
      toast.success(`Imported ${res.data.inserted} · Skipped ${res.data.skipped}`);
      setShowImport(false);
      setImportCSV('');
      fetchData();
    } catch (err) {
      toast.error('Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleSeedStarter = async () => {
    setSeeding(true);
    try {
      const res = await api.post('/admin/leads/seed-starter-pack');
      toast.success(`Seeded ${res.data.inserted} starter leads · ${res.data.skipped} already existed`);
      fetchData();
    } catch (err) {
      toast.error('Seed failed');
    } finally {
      setSeeding(false);
    }
  };

  const filteredLeads = leads.filter((l) => {
    if (filters.search) {
      const q = filters.search.toLowerCase();
      const hay = `${l.name} ${l.sport_type} ${l.city} ${l.phone}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  // Build sport options from current leads
  const sportOptions = Array.from(new Set(leads.map(l => l.sport_type).filter(Boolean))).sort();

  if (loading) return (
    <div className="space-y-4" data-testid="leads-loading">
      <Skeleton className="h-10 w-72" />
      <div className="grid grid-cols-4 gap-3">{[1,2,3,4].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>
      <Skeleton className="h-96 rounded-xl" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="lead-intelligence-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-display font-semibold flex items-center gap-2">
            <Target className="h-6 w-6 text-rose-600" /> Lead Intelligence Engine
          </h1>
          <p className="text-sm text-muted-foreground">India sports academy growth flywheel · {stats?.total ?? leads.length} leads tracked</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {(stats?.total ?? 0) === 0 && (
            <Button onClick={handleSeedStarter} disabled={seeding} variant="outline" data-testid="leads-seed-btn">
              <Sparkles className="h-4 w-4 mr-1.5" />{seeding ? 'Seeding…' : 'Seed 20 Bengaluru Leads'}
            </Button>
          )}
          <Button variant="outline" onClick={() => setShowImport(true)} data-testid="leads-import-btn">
            <Upload className="h-4 w-4 mr-1.5" /> Bulk Import
          </Button>
          {(stats?.total ?? 0) > 0 && (
            <Button
              variant="outline"
              onClick={openBulkPing}
              className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
              data-testid="leads-bulk-ping-btn"
            >
              <Send className="h-4 w-4 mr-1.5" /> Bulk Ping
            </Button>
          )}
          <Button onClick={openCreate} data-testid="leads-add-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Add Lead
          </Button>
        </div>
      </div>

      {/* KPI banner */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="bg-gradient-to-br from-rose-50 to-pink-50 border-rose-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-1"><Flame className="h-4 w-4 text-rose-600" /><Badge variant="outline" className="text-[9px]">Active</Badge></div>
              <p className="text-2xl font-display font-bold tabular-nums text-rose-700" data-testid="leads-kpi-hot">{stats.hot}</p>
              <p className="text-xs text-rose-700">Hot Leads</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-1"><MessageSquare className="h-4 w-4 text-amber-600" /><Badge variant="outline" className="text-[9px]">Pipeline</Badge></div>
              <p className="text-2xl font-display font-bold tabular-nums text-amber-700" data-testid="leads-kpi-demo">{stats.demo_scheduled}</p>
              <p className="text-xs text-amber-700">Demo Scheduled</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-1"><Sparkles className="h-4 w-4 text-emerald-600" /><Badge variant="outline" className="text-[9px]">Closed</Badge></div>
              <p className="text-2xl font-display font-bold tabular-nums text-emerald-700" data-testid="leads-kpi-won">{stats.won}</p>
              <p className="text-xs text-emerald-700">Won</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-slate-50 to-zinc-50 border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-1"><Target className="h-4 w-4 text-slate-600" /><Badge variant="outline" className="text-[9px]">All time</Badge></div>
              <p className="text-2xl font-display font-bold tabular-nums text-slate-700" data-testid="leads-kpi-total">{stats.total}</p>
              <p className="text-xs text-slate-700">Total · {stats.by_tier?.premium || 0} Premium</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <Tabs value={filters.tier} onValueChange={(v) => setFilters({ ...filters, tier: v })}>
          <TabsList>
            <TabsTrigger value="all" data-testid="leads-tab-all">All</TabsTrigger>
            <TabsTrigger value="premium" data-testid="leads-tab-premium">Premium ({stats?.by_tier?.premium || 0})</TabsTrigger>
            <TabsTrigger value="growing" data-testid="leads-tab-growing">Growing ({stats?.by_tier?.growing || 0})</TabsTrigger>
            <TabsTrigger value="small" data-testid="leads-tab-small">Small ({stats?.by_tier?.small || 0})</TabsTrigger>
          </TabsList>
        </Tabs>
        <Select value={filters.stage} onValueChange={(v) => setFilters({ ...filters, stage: v })}>
          <SelectTrigger className="h-9 w-44 text-xs" data-testid="leads-filter-stage"><SelectValue placeholder="All stages" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All stages</SelectItem>
            {STAGES.map(s => <SelectItem key={s} value={s}>{STAGE_LABELS[s]}</SelectItem>)}
          </SelectContent>
        </Select>
        {sportOptions.length > 0 && (
          <Select value={filters.sport} onValueChange={(v) => setFilters({ ...filters, sport: v })}>
            <SelectTrigger className="h-9 w-36 text-xs" data-testid="leads-filter-sport"><SelectValue placeholder="All sports" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sports</SelectItem>
              {sportOptions.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <div className="relative flex-1 min-w-48 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            placeholder="Search name, city, phone, sport…"
            className="pl-9 h-9 text-xs"
            data-testid="leads-search"
          />
        </div>
      </div>

      {/* Phase 89-Lead-Intel-Schedule — Scheduled Campaigns panel */}
      {scheduledCampaigns.length > 0 && (
        <Card className="border-indigo-100 bg-gradient-to-br from-indigo-50/40 to-white" data-testid="scheduled-campaigns-panel">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-indigo-600" />
                <span className="text-xs font-bold text-slate-800 uppercase tracking-wider">Scheduled Drip Campaigns</span>
                <Badge variant="outline" className="text-[9px]">{scheduledCampaigns.filter((c) => c.is_active).length} active · {scheduledCampaigns.length} total</Badge>
              </div>
              <button
                type="button"
                onClick={reloadScheduledCampaigns}
                disabled={campaignsLoading}
                className="text-[10px] text-slate-500 hover:text-slate-700 flex items-center gap-1"
                data-testid="scheduled-campaigns-refresh"
              >
                <RotateCcw className={`h-3 w-3 ${campaignsLoading ? 'animate-spin' : ''}`} /> Refresh
              </button>
            </div>
            <div className="space-y-1.5">
              {scheduledCampaigns.map((c) => {
                const cfg = c.schedule_config || {};
                const shape = c.schedule_kind === 'once'
                  ? `Once · ${cfg.run_at || cfg.run_at_utc || '?'}`
                  : cfg.repeat === 'weekly'
                    ? `Weekly · ${cfg.day_of_week} ${cfg.time_of_day} IST`
                    : cfg.repeat === 'weekday'
                      ? `Weekdays · ${cfg.time_of_day} IST`
                      : `Daily · ${cfg.time_of_day} IST`;
                return (
                  <div key={c.id} className="flex items-center gap-3 px-3 py-2 bg-white border border-slate-100 rounded-lg hover:border-slate-200" data-testid={`scheduled-campaign-row-${c.id}`}>
                    <div className={`h-2 w-2 rounded-full ${c.is_active ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-xs font-semibold text-slate-800 truncate">
                        {c.title}
                        <code className="font-mono text-[9px] px-1 py-0.5 bg-slate-50 text-slate-500 rounded">{c.template_key}</code>
                      </div>
                      <div className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-3">
                        <span className="flex items-center gap-1"><Clock className="h-2.5 w-2.5" /> {shape}</span>
                        {c.next_run_at && c.is_active && (
                          <span className="text-indigo-600">Next: {new Date(c.next_run_at).toLocaleString()}</span>
                        )}
                        {c.run_count > 0 && (<span>Runs: {c.run_count}</span>)}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => handlePauseCampaign(c.id, c.is_active)}
                        title={c.is_active ? 'Pause' : 'Resume'}
                        className="p-1.5 rounded hover:bg-slate-100 text-slate-600"
                        data-testid={`scheduled-campaign-toggle-${c.id}`}
                      >
                        {c.is_active ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5 text-emerald-600" />}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteCampaign(c.id)}
                        title="Delete"
                        className="p-1.5 rounded hover:bg-rose-50 text-rose-600"
                        data-testid={`scheduled-campaign-delete-${c.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {filteredLeads.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground" data-testid="leads-empty">
              <Target className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <p>No leads match the current filters.</p>
              {(stats?.total ?? 0) === 0 && (
                <Button onClick={handleSeedStarter} className="mt-4" data-testid="leads-empty-seed-btn">
                  <Sparkles className="h-4 w-4 mr-1.5" /> Seed 20 Bengaluru Starter Leads
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Academy</TableHead>
                  <TableHead className="hidden md:table-cell">Sport · City</TableHead>
                  <TableHead>Score</TableHead>
                  <TableHead>Tier</TableHead>
                  <TableHead>Stage</TableHead>
                  <TableHead>Temp</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLeads.map((l) => {
                  const tm = tierMeta[l.tier] || tierMeta.small;
                  const TempIcon = tempMeta[l.lead_temperature]?.icon || ThermometerSun;
                  return (
                    <TableRow key={l.id} data-testid={`lead-row-${l.id}`}>
                      <TableCell className="font-medium">
                        <div>
                          <p className="text-sm leading-tight">{l.name}</p>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            {l.website && <a href={l.website} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-teal-600" title={l.website}><Globe className="h-3 w-3" /></a>}
                            {l.instagram && <Instagram className="h-3 w-3 text-rose-400" title={l.instagram} />}
                            {l.phone && <span className="text-[10px] text-muted-foreground">{l.phone}</span>}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-xs">
                        <span className="capitalize">{l.sport_type}</span>
                        {l.city && <span className="text-muted-foreground"><MapPin className="h-3 w-3 inline mx-0.5" />{l.city}</span>}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <div className="h-1.5 w-12 bg-muted rounded-full overflow-hidden">
                            <div className={`h-full ${l.academy_score >= 65 ? 'bg-amber-500' : l.academy_score >= 35 ? 'bg-emerald-500' : 'bg-slate-400'}`} style={{ width: `${l.academy_score}%` }} />
                          </div>
                          <span className="text-xs tabular-nums font-semibold" data-testid={`lead-score-${l.id}`}>{l.academy_score}</span>
                        </div>
                      </TableCell>
                      <TableCell><Badge className={`text-[10px] ${tm.color}`} data-testid={`lead-tier-${l.id}`}><tm.icon className="h-2.5 w-2.5 mr-0.5" />{tm.label}</Badge></TableCell>
                      <TableCell>
                        <Select value={l.pipeline_stage} onValueChange={(v) => handleStageChange(l.id, v)}>
                          <SelectTrigger className={`h-7 w-32 text-[10px] ${stageMeta[l.pipeline_stage] || ''}`} data-testid={`lead-stage-${l.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{STAGES.map(s => <SelectItem key={s} value={s} className="text-xs">{STAGE_LABELS[s]}</SelectItem>)}</SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${tempMeta[l.lead_temperature]?.color}`}>
                          <TempIcon className="h-3 w-3" />{tempMeta[l.lead_temperature]?.label}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {/* Phase 106 — Convert to Customer button (Won stage or converted leads) */}
                          {l.pipeline_stage === 'won' && !l.converted_academy_id && (
                            <Button size="sm" className="h-7 text-[10px] px-2 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white" onClick={() => handleConvertToCustomer(l)} disabled={convertingId === l.id} data-testid={`lead-convert-${l.id}`}>
                              {convertingId === l.id ? 'Converting…' : '🚀 Onboard'}
                            </Button>
                          )}
                          {l.converted_academy_id && (
                            <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-300" title={`Academy ${l.converted_academy_id.slice(0, 8)}…`} data-testid={`lead-converted-${l.id}`}>✓ Customer</Badge>
                          )}
                          <Button size="sm" variant="outline" className="h-7 w-7 p-0 text-emerald-700 border-emerald-300 hover:bg-emerald-50" onClick={() => handleWhatsApp(l)} disabled={!l.phone} title={l.phone ? 'Send WhatsApp pitch' : 'No phone'} data-testid={`lead-whatsapp-${l.id}`}><MessageSquare className="h-3.5 w-3.5" /></Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className={`h-7 w-7 p-0 ${l.opt_in_consent
                              ? 'text-emerald-700 border-emerald-400 hover:bg-emerald-50'
                              : 'text-slate-300 border-slate-200 cursor-not-allowed'}`}
                            onClick={() => handleInstantPing(l)}
                            disabled={!l.phone || !l.opt_in_consent || pingingId === l.id}
                            title={
                              !l.phone ? 'No phone' :
                              !l.opt_in_consent ? 'Requires explicit WhatsApp opt-in (edit the lead to capture consent)' :
                              `Instant AiSensy welcome ping → ${l.phone}`
                            }
                            data-testid={`lead-ping-${l.id}`}
                          >
                            <Zap className={`h-3.5 w-3.5 ${pingingId === l.id ? 'animate-pulse' : ''}`} />
                          </Button>
                          {l.phone && <Button size="sm" variant="outline" className="h-7 w-7 p-0" asChild title={`Call ${l.phone}`} data-testid={`lead-call-${l.id}`}><a href={`tel:${l.phone}`}><Phone className="h-3.5 w-3.5" /></a></Button>}
                          {l.email && <Button size="sm" variant="outline" className="h-7 w-7 p-0" asChild title={`Email ${l.email}`}><a href={`mailto:${l.email}`}><Mail className="h-3.5 w-3.5" /></a></Button>}
                          <Button size="sm" variant="ghost" className="h-7 text-[10px] px-2" onClick={() => openEdit(l)} data-testid={`lead-edit-${l.id}`}>Edit</Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={showForm} onOpenChange={(v) => { setShowForm(v); if (!v) { setEditing(null); setForm(emptyForm()); } }}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="lead-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Lead' : 'Add Lead'}</DialogTitle>
            <DialogDescription>Score auto-recomputes after save.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5 col-span-2"><Label className="text-xs">Academy Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="lead-form-name" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Sport Type</Label><Input value={form.sport_type} onChange={(e) => setForm({ ...form, sport_type: e.target.value })} placeholder="Football, Cricket, Multi Sports…" data-testid="lead-form-sport" /></div>
              <div className="space-y-1.5"><Label className="text-xs">City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="lead-form-city" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91XXXXXXXXXX" data-testid="lead-form-phone" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="lead-form-email" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Website</Label><Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://…" data-testid="lead-form-website" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Instagram</Label><Input value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value })} data-testid="lead-form-instagram" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Google Reviews Count</Label><Input type="number" min="0" value={form.reviews_count} onChange={(e) => setForm({ ...form, reviews_count: parseInt(e.target.value) || 0 })} data-testid="lead-form-reviews" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Outreach Platform</Label>
                <Select value={form.outreach_platform} onValueChange={(v) => setForm({ ...form, outreach_platform: v })}>
                  <SelectTrigger data-testid="lead-form-platform"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['WhatsApp', 'WhatsApp + Instagram', 'WhatsApp + Call', 'Instagram DM', 'Instagram + WhatsApp', 'Instagram + Email', 'LinkedIn + Email', 'Phone Call', 'Email'].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-4 flex-wrap pt-2">
              <label className="flex items-center gap-1.5 text-xs cursor-pointer"><input type="checkbox" checked={form.has_multi_branch} onChange={(e) => setForm({ ...form, has_multi_branch: e.target.checked })} data-testid="lead-form-multibranch" />Multi-branch</label>
              <label className="flex items-center gap-1.5 text-xs cursor-pointer"><input type="checkbox" checked={form.multi_sport} onChange={(e) => setForm({ ...form, multi_sport: e.target.checked })} data-testid="lead-form-multisport" />Multi-sport</label>
              <label className="flex items-center gap-1.5 text-xs cursor-pointer"><input type="checkbox" checked={form.has_turf} onChange={(e) => setForm({ ...form, has_turf: e.target.checked })} data-testid="lead-form-turf" />Has Turf / Infrastructure</label>
              <label className="flex items-center gap-1.5 text-xs cursor-pointer"><input type="checkbox" checked={form.active_social_media} onChange={(e) => setForm({ ...form, active_social_media: e.target.checked })} data-testid="lead-form-social" />Active Social Media</label>
            </div>
            <div className="flex items-start gap-2 p-2.5 rounded-lg bg-emerald-50 border border-emerald-200">
              <input
                id="lead-form-optin"
                type="checkbox"
                className="mt-0.5"
                checked={form.opt_in_consent}
                onChange={(e) => setForm({ ...form, opt_in_consent: e.target.checked })}
                data-testid="lead-form-optin"
              />
              <label htmlFor="lead-form-optin" className="text-[11px] font-medium text-emerald-900 cursor-pointer flex-1 leading-tight">
                <span className="font-bold">WhatsApp Opt-In Consent captured</span>
                <span className="block text-[10px] text-emerald-700/80 mt-0.5">
                  Required by Meta WhatsApp Business policy before sending automated outbound messages.
                  Must be explicitly granted by the lead (e.g. checkbox on inbound form, written consent in chat).
                </span>
              </label>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Anything we should remember about this lead…" data-testid="lead-form-notes" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="lead-form-save">{saving ? 'Saving…' : (editing ? 'Update' : 'Add Lead')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Import dialog */}
      <Dialog open={showImport} onOpenChange={setShowImport}>
        <DialogContent className="max-w-xl" data-testid="lead-import-dialog">
          <DialogHeader>
            <DialogTitle>Bulk Import Leads</DialogTitle>
            <DialogDescription>One lead per line, CSV format: <code className="bg-muted px-1 rounded">name,sport,phone,city,website,reviews</code></DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Textarea
              value={importCSV}
              onChange={(e) => setImportCSV(e.target.value)}
              rows={10}
              className="font-mono text-xs"
              placeholder={`Blues Football Academy,Football,+918553159146,Bengaluru,,45\nGameFit Academy,Multi Sports,,Bengaluru,https://www.gamefit.ai/,200`}
              data-testid="lead-import-textarea"
            />
            <p className="text-[11px] text-muted-foreground">Score, tier &amp; eligibility are auto-derived. Duplicates (same name+phone) are skipped.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImport(false)}>Cancel</Button>
            <Button onClick={handleBulkImport} disabled={importing} data-testid="lead-import-submit"><Upload className="h-3.5 w-3.5 mr-1.5" />{importing ? 'Importing…' : 'Import'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Phase 106 — Conversion result modal */}
      <Dialog open={!!conversionResult} onOpenChange={(v) => { if (!v) setConversionResult(null); }}>
        <DialogContent className="max-w-md" data-testid="lead-conversion-dialog">
          <DialogHeader>
            <DialogTitle>🎉 Lead → Customer</DialogTitle>
            <DialogDescription>The academy has been provisioned and an invite dispatched.</DialogDescription>
          </DialogHeader>
          {conversionResult && (
            <div className="space-y-3 text-sm">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                <p className="font-medium text-emerald-900">{conversionResult.lead.name}</p>
                <p className="text-xs text-emerald-700 mt-0.5">Slug: <code className="bg-white px-1 rounded">{conversionResult.result.slug}</code></p>
                <p className="text-xs text-emerald-700">Academy ID: <code className="bg-white px-1 rounded text-[10px]">{conversionResult.result.academy_id}</code></p>
              </div>
              <div>
                <Label className="text-xs">Magic-login link (valid 7 days)</Label>
                <div className="flex gap-1 mt-1">
                  <Input readOnly value={conversionResult.result.magic_link} className="text-[11px] font-mono" data-testid="conversion-magic-link" />
                  <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(conversionResult.result.magic_link); toast.success('Magic link copied'); }} data-testid="conversion-copy-link">Copy</Button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="rounded border p-2">
                  <p className="font-medium mb-0.5">WhatsApp dispatch</p>
                  <p className="text-muted-foreground">{conversionResult.result.dispatched.whatsapp ? '✓ Sent (or queued in mocked relay)' : '— Skipped (no phone)'}</p>
                </div>
                <div className="rounded border p-2">
                  <p className="font-medium mb-0.5">Email dispatch</p>
                  <p className="text-muted-foreground">{conversionResult.result.dispatched.email ? '✓ Sent (or queued in mocked relay)' : '— Skipped (no email)'}</p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setConversionResult(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 89-Lead-Intel-Bulk — Bulk WhatsApp Ping modal */}
      <Dialog open={showBulkPing} onOpenChange={(v) => { if (!v) closeBulkPing(); else setShowBulkPing(true); }}>
        <DialogContent className="max-w-lg" data-testid="bulk-ping-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-indigo-600" /> Bulk WhatsApp Ping
            </DialogTitle>
            <DialogDescription>
              Fires a Meta-approved template to every opted-in lead matching the segment. Leads
              without explicit <code className="font-mono text-[10px] px-1 bg-slate-100 rounded">opt_in_consent</code> are skipped automatically.
            </DialogDescription>
          </DialogHeader>

          {/* SEGMENT + TEMPLATE PICKER — only when no batch in flight */}
          {!bulkBatch && (
            <div className="space-y-4">
              {/* Mode toggle: Send Now vs Schedule */}
              <div className="flex gap-1.5 p-1 bg-slate-100 rounded-lg" data-testid="bulk-ping-mode-toggle">
                <button
                  type="button"
                  onClick={() => setBulkMode('now')}
                  className={`flex-1 py-1.5 px-2 rounded-md text-[11px] font-bold tracking-wide transition-all ${bulkMode === 'now' ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                  data-testid="bulk-ping-mode-now"
                >
                  ⚡ Send Now
                </button>
                <button
                  type="button"
                  onClick={() => setBulkMode('schedule')}
                  className={`flex-1 py-1.5 px-2 rounded-md text-[11px] font-bold tracking-wide transition-all ${bulkMode === 'schedule' ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                  data-testid="bulk-ping-mode-schedule"
                >
                  📅 Schedule + Repeat
                </button>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">Meta Template</Label>
                <Select value={bulkFilters.template_key} onValueChange={(v) => setBulkFilters({ ...bulkFilters, template_key: v })}>
                  <SelectTrigger className="h-9 text-xs font-mono" data-testid="bulk-ping-template">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BULK_TEMPLATES.map((k) => (<SelectItem key={k} value={k} className="text-xs font-mono">{k}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">Temperature</Label>
                  <Select value={bulkFilters.temperature} onValueChange={(v) => setBulkFilters({ ...bulkFilters, temperature: v })}>
                    <SelectTrigger className="h-9 text-xs" data-testid="bulk-ping-temperature">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any" className="text-xs">Any temp</SelectItem>
                      <SelectItem value="hot" className="text-xs">🔥 Hot</SelectItem>
                      <SelectItem value="warm" className="text-xs">🌡️ Warm</SelectItem>
                      <SelectItem value="cold" className="text-xs">❄️ Cold</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">Stage</Label>
                  <Select value={bulkFilters.pipeline_stage} onValueChange={(v) => setBulkFilters({ ...bulkFilters, pipeline_stage: v })}>
                    <SelectTrigger className="h-9 text-xs" data-testid="bulk-ping-stage">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any" className="text-xs">Any stage</SelectItem>
                      {STAGES.map((s) => (<SelectItem key={s} value={s} className="text-xs">{STAGE_LABELS[s]}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">Tier</Label>
                  <Select value={bulkFilters.tier} onValueChange={(v) => setBulkFilters({ ...bulkFilters, tier: v })}>
                    <SelectTrigger className="h-9 text-xs" data-testid="bulk-ping-tier">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any" className="text-xs">Any tier</SelectItem>
                      <SelectItem value="premium" className="text-xs">Premium</SelectItem>
                      <SelectItem value="growing" className="text-xs">Growing</SelectItem>
                      <SelectItem value="small" className="text-xs">Small</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-3" data-testid="bulk-ping-preview">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-indigo-900">Eligible opted-in leads</span>
                  {bulkPreview.loading ? (
                    <Loader2 className="h-4 w-4 animate-spin text-indigo-500" />
                  ) : (
                    <span className="text-2xl font-display font-bold text-indigo-700 tabular-nums" data-testid="bulk-ping-eligible-count">
                      {bulkPreview.eligible}
                    </span>
                  )}
                </div>
                {bulkPreview.sample_names.length > 0 && (
                  <p className="text-[11px] text-indigo-700/80 mt-1">
                    Sample: {bulkPreview.sample_names.join(' · ')}{bulkPreview.eligible > bulkPreview.sample_names.length ? ' …' : ''}
                  </p>
                )}
                {bulkPreview.eligible === 0 && !bulkPreview.loading && (
                  <p className="text-[11px] text-rose-600 mt-1">
                    No leads with explicit WhatsApp opt-in match. Capture consent via the lead edit dialog first.
                  </p>
                )}
              </div>

              {/* Schedule configuration — only when bulkMode === 'schedule' */}
              {bulkMode === 'schedule' && (
                <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-indigo-50/30 to-white p-3 space-y-3" data-testid="bulk-ping-schedule-block">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-indigo-600" />
                    <span className="text-xs font-bold text-slate-800">Schedule Configuration</span>
                  </div>
                  <div className="flex gap-1.5">
                    <button
                      type="button"
                      onClick={() => setSchedulePayload({ ...schedulePayload, kind: 'once' })}
                      className={`flex-1 py-1.5 rounded-lg border text-[11px] font-bold transition-all ${schedulePayload.kind === 'once' ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}
                      data-testid="schedule-kind-once"
                    >
                      Once
                    </button>
                    <button
                      type="button"
                      onClick={() => setSchedulePayload({ ...schedulePayload, kind: 'recurring' })}
                      className={`flex-1 py-1.5 rounded-lg border text-[11px] font-bold transition-all ${schedulePayload.kind === 'recurring' ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}
                      data-testid="schedule-kind-recurring"
                    >
                      Recurring
                    </button>
                  </div>

                  {schedulePayload.kind === 'once' ? (
                    <div className="space-y-1">
                      <Label className="text-[10px] font-bold uppercase text-slate-500 tracking-wider">Run At (IST)</Label>
                      <input
                        type="datetime-local"
                        value={schedulePayload.run_at_local}
                        onChange={(e) => setSchedulePayload({ ...schedulePayload, run_at_local: e.target.value })}
                        className="w-full text-xs px-2 py-2 bg-white border border-slate-300 rounded-lg font-mono"
                        data-testid="schedule-run-at"
                      />
                    </div>
                  ) : (
                    <>
                      <div className="space-y-1">
                        <Label className="text-[10px] font-bold uppercase text-slate-500 tracking-wider">Repeat Mode</Label>
                        <Select value={schedulePayload.repeat} onValueChange={(v) => setSchedulePayload({ ...schedulePayload, repeat: v })}>
                          <SelectTrigger className="h-9 text-xs" data-testid="schedule-repeat">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="daily" className="text-xs">Daily</SelectItem>
                            <SelectItem value="weekday" className="text-xs">Every weekday (Mon–Fri)</SelectItem>
                            <SelectItem value="weekly" className="text-xs">Weekly on a specific day</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {schedulePayload.repeat === 'weekly' && (
                          <div className="space-y-1">
                            <Label className="text-[10px] font-bold uppercase text-slate-500 tracking-wider">Day</Label>
                            <Select value={schedulePayload.day_of_week} onValueChange={(v) => setSchedulePayload({ ...schedulePayload, day_of_week: v })}>
                              <SelectTrigger className="h-9 text-xs" data-testid="schedule-day-of-week">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {DAY_OPTIONS.map((d) => (<SelectItem key={d} value={d} className="text-xs">{d}</SelectItem>))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        <div className={`space-y-1 ${schedulePayload.repeat === 'weekly' ? '' : 'col-span-2'}`}>
                          <Label className="text-[10px] font-bold uppercase text-slate-500 tracking-wider">Time (IST)</Label>
                          <Select value={schedulePayload.time_of_day} onValueChange={(v) => setSchedulePayload({ ...schedulePayload, time_of_day: v })}>
                            <SelectTrigger className="h-9 text-xs font-mono" data-testid="schedule-time">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {TIME_OPTIONS.map((t) => (<SelectItem key={t} value={t} className="text-xs font-mono">{t}</SelectItem>))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* PROGRESS — once a batch is live */}
          {bulkBatch && (
            <div className="space-y-4" data-testid="bulk-ping-progress">
              <div className="rounded-xl border border-slate-200 p-4 bg-gradient-to-br from-slate-50 to-white">
                <div className="flex items-center gap-2 mb-3">
                  {bulkBatch.status === 'complete' ? (
                    <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                  ) : (
                    <Loader2 className="h-5 w-5 animate-spin text-indigo-600" />
                  )}
                  <span className="text-sm font-semibold text-slate-900">
                    {bulkBatch.status === 'complete' ? 'Dispatch complete' : 'Dispatching…'}
                  </span>
                  <span className="ml-auto text-[10px] font-mono text-slate-400">batch:{bulkBatch.batch_id.slice(0, 8)}</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden mb-3">
                  <div
                    className="h-full bg-gradient-to-r from-indigo-500 to-fuchsia-500 transition-all"
                    style={{ width: `${Math.min(100, ((bulkBatch.processed || 0) / Math.max(1, bulkBatch.queued_count)) * 100)}%` }}
                    data-testid="bulk-ping-progress-bar"
                  />
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-lg bg-white border p-2">
                    <p className="text-[9px] text-slate-500 uppercase tracking-wider font-bold">Processed</p>
                    <p className="text-lg font-bold tabular-nums text-slate-900" data-testid="bulk-ping-processed">
                      {bulkBatch.processed || 0}<span className="text-xs text-slate-400">/{bulkBatch.queued_count}</span>
                    </p>
                  </div>
                  <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-2">
                    <p className="text-[9px] text-emerald-700 uppercase tracking-wider font-bold">Sent</p>
                    <p className="text-lg font-bold tabular-nums text-emerald-700" data-testid="bulk-ping-success">
                      {bulkBatch.success_count || 0}
                    </p>
                  </div>
                  <div className="rounded-lg bg-rose-50 border border-rose-200 p-2">
                    <p className="text-[9px] text-rose-700 uppercase tracking-wider font-bold">Failed</p>
                    <p className="text-lg font-bold tabular-nums text-rose-700" data-testid="bulk-ping-failed">
                      {bulkBatch.failure_count || 0}
                    </p>
                  </div>
                </div>
                {(bulkBatch.skipped_consent > 0 || bulkBatch.skipped_phone > 0) && (
                  <p className="text-[10px] text-slate-500 mt-2">
                    Skipped at pre-dispatch: {bulkBatch.skipped_consent} no-consent · {bulkBatch.skipped_phone} no-phone
                  </p>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            {!bulkBatch ? (
              <>
                <Button variant="outline" onClick={closeBulkPing} data-testid="bulk-ping-cancel">Cancel</Button>
                {bulkMode === 'now' ? (
                  <Button
                    onClick={handleLaunchBulkPing}
                    disabled={bulkLaunching || bulkPreview.loading || bulkPreview.eligible === 0}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white"
                    data-testid="bulk-ping-launch"
                  >
                    {bulkLaunching ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Queuing…</> : <><Send className="h-4 w-4 mr-1.5" /> Launch · {bulkPreview.eligible} leads</>}
                  </Button>
                ) : (
                  <Button
                    onClick={handleSaveSchedule}
                    disabled={
                      scheduleSubmitting ||
                      (schedulePayload.kind === 'once' && !schedulePayload.run_at_local)
                    }
                    className="bg-indigo-600 hover:bg-indigo-700 text-white"
                    data-testid="bulk-ping-save-schedule"
                  >
                    {scheduleSubmitting ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Saving…</> : <><Calendar className="h-4 w-4 mr-1.5" /> Save Schedule</>}
                  </Button>
                )}
              </>
            ) : (
              <Button
                onClick={closeBulkPing}
                disabled={bulkBatch.status !== 'complete'}
                className={bulkBatch.status === 'complete' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}
                data-testid="bulk-ping-close"
              >
                {bulkBatch.status === 'complete' ? <><CheckCircle2 className="h-4 w-4 mr-1.5" /> Done</> : <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Dispatching…</>}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
